import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import FlyCodeBanner from "../components/home/FlyCodeBanner";
import FooterSection from "../components/home/FooterSection";
import chartBarIcon from "../images/F-Chart Bar.png";
import phoneIcon from "../images/F-Phone.png";
import clockIcon from "../images/L-Clock.png";
import maskGroupIcon from "../images/Mask group.png";
import "../styles/enterprise.css";

// Styled Savings Card Component
const StyledSavingsCard = styled.div`
  .outer {
    width: 300px;
    max-width: 100%;
    height: 250px;
    border-radius: 10px;
    padding: 1px;
    background: radial-gradient(circle 230px at 0% 0%, #ffffff, #0c0d0d);
    position: relative;
  }

  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    width: 100%;

    .outer {
      width: calc(100vw - 48px);
      max-width: 380px;
      height: 280px;
      overflow: hidden;
    }
    .card {
      overflow: hidden;
    }
    .card .text {
      font-size: 3.25rem;
    }
    .card .label {
      font-size: 0.85rem;
      letter-spacing: 2px;
    }
  }

  @media (max-width: 350px) {
    .outer {
      width: calc(100vw - 32px);
      height: 240px;
    }
    .card .text {
      font-size: 2.5rem;
    }
  }

  .card {
    z-index: 1;
    width: 100%;
    height: 100%;
    border-radius: 9px;
    border: solid 1px #202222;
    background-size: 20px 20px;
    background: radial-gradient(circle 280px at 0% 0%, #444444, #0c0d0d);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    flex-direction: column;
    color: #fff;
  }

  .ray {
    width: 220px;
    height: 45px;
    border-radius: 100px;
    position: absolute;
    background-color: #c7c7c7;
    opacity: 0.4;
    box-shadow: 0 0 50px #fff;
    filter: blur(10px);
    transform-origin: 10%;
    top: 0%;
    left: 0;
    transform: rotate(40deg);
  }

  .card .label {
    font-size: 0.75rem;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    margin-bottom: 8px;
  }

  .card .text {
    font-weight: bolder;
    font-size: 2.75rem;
    background: linear-gradient(45deg, #000000 4%, #fff, #000);
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
  }

  .line {
    width: 100%;
    height: 1px;
    position: absolute;
    background-color: #2c2c2c;
  }

  .topl {
    top: 10%;
    background: linear-gradient(90deg, #888888 30%, #1d1f1f 70%);
  }

  .bottoml {
    bottom: 10%;
  }

  .leftl {
    left: 10%;
    width: 1px;
    height: 100%;
    background: linear-gradient(180deg, #747474 30%, #222424 70%);
  }

  .rightl {
    right: 10%;
    width: 1px;
    height: 100%;
  }
`;

// Styled Slider Component
const StyledSlider = styled.div`
  width: 100%;
  margin-top: 12px;

  .slider {
    width: 100%;
    display: block;
  }

  .slider .level {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    width: 100%;
    height: 6px;
    border-radius: 999px;
    cursor: pointer;
    outline: none;
    background: linear-gradient(
      to right,
      #fff 0%,
      #fff var(--fill, 0%),
      rgb(82, 82, 82) var(--fill, 0%),
      rgb(82, 82, 82) 100%
    );
  }

  .slider .level::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    background: #fff;
    border-radius: 50%;
    cursor: grab;
    border: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  .slider .level::-webkit-slider-thumb:active {
    cursor: grabbing;
  }

  .slider .level::-moz-range-thumb {
    width: 14px;
    height: 14px;
    background: #fff;
    border-radius: 50%;
    cursor: grab;
    border: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  .slider .level::-moz-range-thumb:active {
    cursor: grabbing;
  }
`;

const Greek = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [chapterSize, setChapterSize] = useState(5);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const calculateChapterSavings = (members) => {
    // Baseline: 10 tools at $20/month each for 12 months = $2,400 per member per year
    const annualSavingsPerMember = 2400;
    const totalSavings = members * annualSavingsPerMember;
    return totalSavings;
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M+`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K+`;
    }
    return `$${amount}+`;
  };

  const useCases = [
    {
      title: "Productivity",
      description:
        "The right tools help students focus, increase efficiency/output, and improve academic performance.",
      link: "/productivity",
      icon: chartBarIcon,
    },
    {
      title: "Save Big",
      description:
        "Students will save $2400+ per year with an all-in-one suite of study tools and resources.",
      link: "/savings",
      icon: phoneIcon,
    },
    {
      title: "Time",
      description:
        "Students then have time for what matters: less stress, more freedom.",
      link: "/time",
      icon: clockIcon,
    },
    {
      title: "Empowerment",
      description:
        "Any student can thrive if well equipped with the right resources.",
      link: "/empowerment",
      icon: maskGroupIcon,
    },
  ];

  const features = [
    {
      title: "Company knowledge base.",
      description:
        "Centralize prompts and documents reps can access instantly.",
    },
    {
      title: "Team-level settings & permissions.",
      description:
        "Organize users into teams with custom prompts and controls for tailored use.",
    },
    {
      title: "AI meeting analytics.",
      description:
        "View detailed AI meeting analytics to identify top reps and areas for improvement.",
    },
  ];

  const securityCompliance = [
    {
      title: "ISO 27001 certified",
      description:
        "International standard for information security management.",
    },
    {
      title: "SOC 2 Type I & II compliant",
      description: "Proven long-term data security through independent audits.",
    },
    {
      title: "GDPR compliance",
      description: "Working to align with EU data protection requirements.",
    },
    {
      title: "CCPA compliance",
      description: "Working to meet California's privacy protection standards.",
    },
  ];

  const technicalSecurity = [
    {
      title: "Data encryption at rest and in transit.",
      description: "Encryption protects data stored and transferred.",
    },
    {
      title: "Dedicated workspace isolation.",
      description: "Organizations are isolated for team data security.",
    },
  ];

  return (
    <div className="enterprise-page">
      <FlyCodeBanner />
      <Navbar />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">GREEK</h1>
            <h2 className="hero-heading">
              {isMobile ? (
                <>
                  The foundation
                  <br />
                  successful chapters
                  <br />
                  are built on.
                </>
              ) : (
                <>
                  The foundation successful
                  <br />
                  chapters are built on.
                </>
              )}
            </h2>
            <div className="hero-actions">
              <p className="hero-description">
                Discover how your chapter can thrive when each and every member
                is given the right tools to succeed.
              </p>
              <Link to="/contact-form" className="cta-button">Talk to sales</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases-section">
        <div className="container">
          <h2 className="section-heading">
            {isMobile
              ? "Student success begins with the right support"
              : "Student achievement begins with the right support and resources."}
          </h2>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <div className="use-case-icon">
                  <img
                    src={useCase.icon}
                    alt={useCase.title}
                    style={{ width: "40px", height: "40px" }}
                  />
                </div>
                <div className="use-case-content">
                  <h3 className="use-case-title">{useCase.title}</h3>
                  <p className="use-case-description">{useCase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revenue Calculator Section */}
      <section className="revenue-calculator-section">
        {/* Desktop wave - more detail */}
        <div className="calculator-wave-top desktop-wave">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              className="shape-fill"
            ></path>
          </svg>
        </div>
        {/* Mobile wave - simple, breathable */}
        <div className="calculator-wave-top mobile-wave">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 Q600,120 1200,0 L1200,0 L0,0 Z"
              className="shape-fill"
            ></path>
          </svg>
        </div>
        <div className="container">
          <div className="calculator-content">
            {isMobile ? (
              <>
                <h1
                  className="calculator-title"
                  style={{ marginBottom: "48px" }}
                >
                  How much money does your chapter save with EduTools?
                </h1>
                <div className="calculator-widget">
                  <div
                    className="calculator-layout"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "48px",
                      padding: "0 8px",
                    }}
                  >
                    <StyledSavingsCard>
                      <div className="outer">
                        <div className="card">
                          <div className="ray" />
                          <div className="label">Annual Chapter Savings</div>
                          <div className="text">
                            {formatCurrency(
                              calculateChapterSavings(chapterSize)
                            )}
                          </div>
                          <div className="line topl" />
                          <div className="line leftl" />
                          <div className="line bottoml" />
                          <div className="line rightl" />
                        </div>
                      </div>
                    </StyledSavingsCard>

                    <div style={{ width: "100%", maxWidth: "380px" }}>
                      <div
                        className="current-value"
                        style={{
                          marginBottom: "12px",
                          textAlign: "left",
                          fontSize: "0.875rem",
                          color: "#9ca3af",
                          letterSpacing: "0.5px",
                        }}
                      >
                        <span className="value-label">
                          Member count: {chapterSize}
                        </span>
                      </div>
                      <StyledSlider>
                        <label className="slider">
                          <input
                            type="range"
                            className="level"
                            min="5"
                            max="500"
                            step="5"
                            value={chapterSize}
                            onChange={(e) =>
                              setChapterSize(parseInt(e.target.value))
                            }
                            style={{
                              "--fill": `${
                                ((chapterSize - 5) / (500 - 5)) * 100
                              }%`,
                            }}
                          />
                        </label>
                      </StyledSlider>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="calculator-widget">
                <h1 className="calculator-title">
                  How much money does your chapter save with EduTools?
                </h1>

                <div className="calculator-layout">
                  <div className="slider-section">
                    <div
                      className="current-value"
                      style={{ marginBottom: "8px" }}
                    >
                      <span className="value-label">
                        Member count: {chapterSize}
                      </span>
                    </div>
                    <StyledSlider>
                      <label className="slider">
                        <input
                          type="range"
                          className="level"
                          min="5"
                          max="500"
                          step="5"
                          value={chapterSize}
                          onChange={(e) =>
                            setChapterSize(parseInt(e.target.value))
                          }
                          style={{
                            "--fill": `${
                              ((chapterSize - 5) / (500 - 5)) * 100
                            }%`,
                          }}
                        />
                      </label>
                    </StyledSlider>
                  </div>

                  <StyledSavingsCard>
                    <div className="outer">
                      <div className="card">
                        <div className="ray" />
                        <div className="label">Annual Chapter Savings</div>
                        <div className="text">
                          {formatCurrency(calculateChapterSavings(chapterSize))}
                        </div>
                        <div className="line topl" />
                        <div className="line leftl" />
                        <div className="line bottoml" />
                        <div className="line rightl" />
                      </div>
                    </div>
                  </StyledSavingsCard>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <div
        style={{
          background: "linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)",
        }}
      >
        <FooterSection />
      </div>
    </div>
  );
};

export default Greek;
